/**
 * B638 (e) — CUSTOMER REACH (§11): the facts no agent loop can invent.
 *
 * `customer.search` answers per-customer aggregates (orders, spend,
 * products, showtimes bought and attended, billing state) folded from the
 * customer-fact projection, joined with the customer row for the human
 * facts (name, phone, consent). MANAGER/ADMIN only — buyer PII is
 * back-office truth (the B20b wall). The billing state is the ONLY state
 * (ruling 2026-10-06, option (i)): viewer geo never answers "customers
 * from California".
 *
 * Segments are SAVED FILTERS on the tenant's ops config — a name for a
 * filter, never a cached member list. `sinceDays` is deliberately relative
 * ("the last 30 days" stays true as time moves).
 *
 * Membrane discipline (B633a's lesson): no zod transforms — regex for the
 * blank wall, enums and plain bounds everywhere, so every door translates.
 */
import { z } from "zod";
import { readOp, writeOp, zEpochMs, zId } from "./common.js";

const zState = z.string().regex(/^[A-Za-z]{2}$/);
const zSegmentName = z.string().regex(/\S/).max(60);
const zSinceDays = z.number().int().min(1).max(3650);

const zCustomerReachRow = z
  .object({
    customerId: zId,
    /** Absent when the customer row was erased (B104) — the facts outlive the address. */
    email: z.string().optional(),
    name: z.string().optional(),
    phone: z.string().optional(),
    marketingConsent: z.boolean().optional(),
    /** The latest sale's billing state/country — absent is honest, never guessed. */
    state: z.string().optional(),
    country: z.string().optional(),
    orders: z.number().int().min(0),
    spent: z.array(z.object({ amount: z.number().int(), currency: z.string() }).strict()),
    products: z.array(z.object({ productId: zId, quantity: z.number().int().min(0) }).strict()),
    showtimesBought: z.number().int().min(0),
    showtimesAttended: z.number().int().min(0),
    firstOrderAt: zEpochMs.optional(),
    lastOrderAt: zEpochMs.optional(),
  })
  .strict();

const exampleRow = {
  customerId: "c-1",
  email: "ada@example.com",
  name: "Ada Lovelace",
  marketingConsent: true,
  state: "CA",
  country: "US",
  orders: 3,
  spent: [{ amount: 18_000, currency: "USD" }],
  products: [{ productId: "p-headliner", quantity: 6 }],
  showtimesBought: 3,
  showtimesAttended: 2,
  firstOrderAt: 1_780_000_000_000,
  lastOrderAt: 1_782_000_000_000,
};

export const customerSearch = readOp({
  name: "customer.search",
  permission: "order:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: {
    /** Two-letter billing state ("CA"); matched case-insensitively. */
    state: zState.optional(),
    /** Only facts at or after this instant feed the fold (and the filter). */
    since: zEpochMs.optional(),
    /** Narrow to customers who bought this product. */
    product: zId.optional(),
    /** A saved segment's filter; explicit fields above override its parts. */
    segment: zId.optional(),
    limit: z.number().int().min(1).max(500).optional(),
  },
  response: z
    .object({
      customers: z.array(zCustomerReachRow),
      /** Matches before the limit cap — "showing 100 of 240" stays honest. */
      matched: z.number().int().min(0),
    })
    .strict(),
  exampleRequest: { state: "CA", since: 1_779_000_000_000 },
  exampleResponse: { customers: [exampleRow], matched: 1 },
});

const zSegment = z
  .object({
    segmentId: zId,
    name: zSegmentName,
    state: z.string().optional(),
    sinceDays: zSinceDays.optional(),
    productId: zId.optional(),
    createdAt: zEpochMs,
    updatedAt: zEpochMs,
  })
  .strict();

const exampleSegment = {
  segmentId: "seg-1",
  name: "California buyers, last 30 days",
  state: "CA",
  sinceDays: 30,
  createdAt: 1_780_000_000_000,
  updatedAt: 1_780_000_000_000,
};

export const segmentSave = writeOp({
  name: "segment.save",
  // Tenant configuration write — the channel-config scope (ADMIN-only), the ops.config.set wall.
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: {
    /** Omitted mints a new segment; present updates that one in place. */
    segmentId: zId.optional(),
    name: zSegmentName,
    state: zState.optional(),
    sinceDays: zSinceDays.optional(),
    productId: zId.optional(),
  },
  response: z.object({ segment: zSegment }).strict(),
  exampleRequest: { name: "California buyers, last 30 days", state: "CA", sinceDays: 30 },
  exampleResponse: { segment: exampleSegment },
});

export const segmentList = readOp({
  name: "segment.list",
  permission: "order:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: {},
  response: z.object({ segments: z.array(zSegment) }).strict(),
  exampleRequest: {},
  exampleResponse: { segments: [exampleSegment] },
});

export const segmentRemove = writeOp({
  name: "segment.remove",
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: { segmentId: zId },
  response: z.object({ removed: z.boolean() }).strict(),
  exampleRequest: { segmentId: "seg-1" },
  exampleResponse: { removed: true },
});
